import {
  createSearchOrderHistorySectionData,
  OrderHistoryListOption,
} from '@/utils/order-helpers';
import { gql, NetworkStatus } from '@apollo/client';
import { ORDER_ETA_FRAGMENT } from '@/components/orders/order-eta-banner';
import {
  ORDER_FEES_FIELDS_FRAGMENT,
  ORDER_ITEM_PRICE_FIELDS,
} from '@/utils/money';
import {
  ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT,
  ORDER_INVOICE_LINE_ITEM_FRAGMENT,
} from '@/components/orders/hooks/use-get-order';
import { ORDER_STATUS_LABEL_FIELDS } from '@/components/orders/order-status-label';
import {
  OrderHistory_SearchOrdersQuery,
  OrderHistory_SearchOrdersQueryVariables,
  OrderHistoryFilterOperation,
  OrderHistoryFilterType,
} from '@/types';
import { OrderHistoryScope } from '@/enums';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useMemo, useState } from 'react';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from './use-wrapped-query';

const DEFAULT_PAGE_LIMIT = 1000;
const PAST_ORDERS_PAGE_LIMIT = 20;

const ORDER_HISTORY_ORDER_FRAGMENT = gql`
  fragment OrderHistoryListOrderFields on orders {
    ...OrderStatusLabelFields
    ...OrderETAFields
    chat {
      display_item_prices
      uuid
    }
    orders_items {
      ...OrderItemPriceFields
      notes
      uuid
    }
    order_invoices(limit: 1, order_by: { created_at: desc }) {
      invoice_number
      order_invoice_check_in {
        ...OrderInvoiceCheckInFields
      }
      order_invoice_line_items {
        ...OrderInvoiceLineItemFields
      }
      seen
      uuid
    }
    order_invoices_orders(limit: 1, order_by: { created_at: desc }) {
      order_invoice {
        invoice_number
        order_invoice_check_in {
          ...OrderInvoiceCheckInFields
        }
        order_invoice_line_items {
          ...OrderInvoiceLineItemFields
        }
        seen
        uuid
      }
      uuid
    }
    restaurant_desired_delivery_time
    status
    supplier {
      supplier_metadata {
        display_item_prices
        uuid
      }
      uuid
    }
    supplier_accepted_delivery_time
    uuid
  }

  ${ORDER_ETA_FRAGMENT}
  ${ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT}
  ${ORDER_INVOICE_LINE_ITEM_FRAGMENT}
  ${ORDER_ITEM_PRICE_FIELDS}
  ${ORDER_STATUS_LABEL_FIELDS}
`;

export const SEARCH_ORDER_HISTORY_QUERY = gql`
  fragment OrderHistoryListOrderFeesFields on OrderFee {
    amount_at_invoice_micros
    amount_at_order_micros
    display_type
    name
  }

  fragment OrderHistoryListOrderWithFeesFields on OrderWithFees {
    order {
      ...OrderHistoryListOrderFields
    }
    order_fees {
      ...OrderHistoryListOrderFeesFields
    }
  }

  query OrderHistory_SearchOrders(
    $cursor: String
    $filters: [SearchOrderHistoryFilterInput!]!
    $pageSize: Int
    $restaurantUUID: uuid
    $supplierUUID: uuid!
  ) {
    searchOrderHistory(
      cursor: $cursor
      filters: $filters
      page_size: $pageSize
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      cursor
      orders {
        ...OrderHistoryListOrderWithFeesFields
      }
    }
  }

  ${ORDER_HISTORY_ORDER_FRAGMENT}
`;

export const ORDER_HISTORY_QUERY = gql`
  query OrderHistory_Orders(
    $limit: Int
    $offset: Int
    $skipCount: Boolean!
    $where: orders_bool_exp!
  ) {
    orders_aggregate(where: $where) @skip(if: $skipCount) {
      aggregate {
        count
      }
    }
    orders(
      limit: $limit
      offset: $offset
      order_by: [
        # Relay cursors require unique and sequential columns to order by, so
        # supplier_accepted_delivery_time can't be used since it's nullable
        { restaurant_desired_delivery_time: desc }
        { placed_at: desc }
      ]
      where: $where
    ) {
      ...OrderHistoryListOrderFields
      order_fees(where: { display_type: { _eq: "TOTAL" } }) {
        ...OrderFeesFields
      }
    }
  }

  ${ORDER_HISTORY_ORDER_FRAGMENT}
  ${ORDER_FEES_FIELDS_FRAGMENT}
`;

interface OrderHistoryListQueryResult {
  fetchingMore: boolean;
  fetchMore: () => Promise<void>;
  hasResponse: boolean;
  loading: boolean;
  orders: OrderHistoryListOption[];
  refresh: (manual?: boolean) => Promise<void>;
  refreshing: boolean;
}

export const useOrderHistoryListQuery = (
  showPastOrders: boolean,
): OrderHistoryListQueryResult => {
  const { selectedRestaurantUUID, selectedRestaurantRelationship } =
    useAuthenticatedUserContext();
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const { showErrorToast } = useToasts();

  const queryVariables: OrderHistory_SearchOrdersQueryVariables =
    useMemo(() => {
      return {
        filters: [
          {
            operation: OrderHistoryFilterOperation.Equals,
            type: OrderHistoryFilterType.Scope,
            value: showPastOrders
              ? OrderHistoryScope.Past
              : OrderHistoryScope.Upcoming,
          },
        ],
        pageSize: showPastOrders ? PAST_ORDERS_PAGE_LIMIT : DEFAULT_PAGE_LIMIT,
        restaurantUUID: selectedRestaurantUUID,
        supplierUUID: selectedRestaurantRelationship.supplierUUID,
      };
    }, [
      selectedRestaurantRelationship,
      selectedRestaurantUUID,
      showPastOrders,
    ]);

  const { data, fetchMore, loading, hasResponse, networkStatus, refetch } =
    useWrappedQuery<
      OrderHistory_SearchOrdersQuery,
      OrderHistory_SearchOrdersQueryVariables
    >(SEARCH_ORDER_HISTORY_QUERY, {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: queryVariables,
    });

  return useMemo(() => {
    const orders = data?.searchOrderHistory.orders || [];
    return {
      fetchingMore: networkStatus === NetworkStatus.fetchMore,
      fetchMore: async () => {
        const nextCursor = data?.searchOrderHistory.cursor;
        if (nextCursor) {
          await fetchMore<
            OrderHistory_SearchOrdersQuery,
            OrderHistory_SearchOrdersQueryVariables
          >({
            updateQuery: (prevResult, { fetchMoreResult }) => {
              return fetchMoreResult
                ? {
                    ...prevResult,
                    searchOrderHistory: {
                      ...prevResult.searchOrderHistory,
                      ...fetchMoreResult.searchOrderHistory,
                      orders: [
                        ...prevResult.searchOrderHistory.orders,
                        ...fetchMoreResult.searchOrderHistory.orders,
                      ],
                    },
                  }
                : prevResult;
            },
            variables: {
              ...queryVariables,
              cursor: nextCursor,
            },
          });
        }
      },
      hasResponse,
      loading,
      orders: createSearchOrderHistorySectionData(orders),
      refresh: async (manual = false) => {
        try {
          setIsManualRefresh(manual);
          await refetch();
        } catch {
          if (manual) {
            showErrorToast();
          }
        }
        setIsManualRefresh(false);
      },
      refreshing: isManualRefresh && networkStatus === NetworkStatus.refetch,
    };
  }, [
    data,
    fetchMore,
    showErrorToast,
    isManualRefresh,
    hasResponse,
    loading,
    networkStatus,
    queryVariables,
    refetch,
  ]);
};
